import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import { tooltipDefinitions } from "../data/contentData";

const NAVY = "#061D36";
const NAVY_MID = "#0f2c4a";
const BLUE_ACCENT = "#1565C0";
const BLUE_DARK = "#0d47a1";
const TITLE_COLOR = "#0f2c4a";
const NAVY_GRADIENT = `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`;
const INFO_TINT = "rgba(21, 101, 192";
const NAVY_TINT = "rgba(6, 29, 54";

// Global tooltip state - only one can be open at a time
let globalCloseTooltip = null;

// Tooltip overlay component rendered in portal
function TooltipOverlay({ tooltip, position, onClose, isMobile }) {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  return createPortal(
    <>
      {/* Backdrop to catch clicks - fixed to cover viewport */}
      <div
        onClick={onClose}
        aria-hidden="true"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2147483646,
        }}
      />
      {/* Tooltip content - absolute to stay in place when scrolling */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="tooltip-title"
        style={{
          position: "absolute",
          top: position.top,
          left: position.left,
          width: isMobile ? "calc(100vw - 32px)" : 420,
          backgroundColor: "white",
          borderRadius: "10px",
          boxShadow: "0 12px 48px rgba(0,0,0,0.35)",
          border: `1px solid ${INFO_TINT}, 0.2)`,
          overflow: "hidden",
          zIndex: 2147483647,
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "12px 16px",
            background: NAVY_GRADIENT,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span id="tooltip-title" style={{ color: "#ffffff", fontSize: "0.85rem", fontWeight: 700 }}>
            {tooltip.title}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Închide informația"
            style={{
              background: "none",
              border: "none",
              color: "rgba(255,255,255,0.8)",
              cursor: "pointer",
              padding: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "4px",
            }}
          >
            <Icon sx={{ fontSize: "1rem !important", color: "inherit" }}>close</Icon>
          </button>
        </div>
        {/* Content */}
        <div style={{ padding: "16px", maxHeight: "400px", overflowY: "auto" }}>
          {tooltip.richContent ? (
            <div style={{ color: TITLE_COLOR, fontSize: "0.85rem", lineHeight: 1.7 }}>
              {tooltip.richContent.map((block, idx) => {
                if (block.type === "paragraph") {
                  if (block.segments) {
                    return (
                      <p key={idx} style={{ margin: idx === 0 ? 0 : "12px 0 0 0" }}>
                        {block.segments.map((seg, segIdx) => 
                          seg.bold ? <strong key={segIdx}>{seg.text}</strong> : <span key={segIdx}>{seg.text}</span>
                        )}
                      </p>
                    );
                  }
                  return (
                    <p key={idx} style={{ margin: idx === 0 ? 0 : "12px 0 0 0" }}>
                      {block.bold ? <strong>{block.text}</strong> : block.text}
                    </p>
                  );
                }
                if (block.type === "heading") {
                  return (
                    <p key={idx} style={{ margin: "16px 0 8px 0", fontWeight: 700, fontSize: "0.9rem" }}>
                      {block.text}
                    </p>
                  );
                }
                if (block.type === "list") {
                  return (
                    <ul key={idx} style={{ margin: "12px 0 0 0", paddingLeft: "20px" }}>
                      {block.items.map((item, itemIdx) => (
                        <li key={itemIdx} style={{ marginBottom: "6px" }}>{item}</li>
                      ))}
                    </ul>
                  );
                }
                return null;
              })}
            </div>
          ) : (
            <p style={{ margin: 0, color: TITLE_COLOR, fontSize: "0.85rem", lineHeight: 1.7 }}>
              {tooltip.content}
            </p>
          )}
          {tooltip.link && (
            <div style={{ marginTop: "16px", paddingTop: "12px", borderTop: "1px solid rgba(0,0,0,0.08)" }}>
              <a
                href={tooltip.link.href}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: BLUE_ACCENT,
                  textDecoration: "none",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                }}
              >
                <Icon sx={{ fontSize: "0.85rem !important" }}>open_in_new</Icon>
                {tooltip.link.text}
              </a>
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
}

// Info tooltip component - elegant superscript style
function InfoTooltip({ tooltipId }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const isMobile = useMediaQuery("(max-width:768px)");
  const tooltip = tooltipDefinitions[tooltipId];

  if (!tooltip) return null;

  const handleClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
    
    // Close any other open tooltip first
    if (globalCloseTooltip && globalCloseTooltip !== handleClose) {
      globalCloseTooltip();
    }
    
    const rect = event.currentTarget.getBoundingClientRect();
    const scrollX = window.scrollX || window.pageXOffset;
    const scrollY = window.scrollY || window.pageYOffset;
    const tooltipWidth = isMobile ? window.innerWidth - 32 : 420;
    
    // Calculate position in document coordinates (not viewport)
    let left = rect.left + scrollX;
    let top = rect.bottom + scrollY + 8;
    
    // Ensure tooltip doesn't go off-screen on the right (viewport check)
    if (rect.left + tooltipWidth > window.innerWidth - 16) {
      left = scrollX + window.innerWidth - tooltipWidth - 16;
    }
    // Ensure tooltip doesn't go off-screen on the left
    if (left < scrollX + 16) {
      left = scrollX + 16;
    }
    
    setPosition({ top, left });
    setOpen(true);
    globalCloseTooltip = handleClose;
  };

  const handleClose = () => {
    setOpen(false);
    if (globalCloseTooltip === handleClose) {
      globalCloseTooltip = null;
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleClick(event);
    }
  };

  return (
    <>
      <SoftBox
        component="span"
        role="button"
        tabIndex={0}
        aria-label={`Mai multe informații despre ${tooltip.title || "acest termen"}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 14,
          height: 14,
          ml: 0.3,
          fontSize: "0.6rem",
          fontWeight: 600,
          fontStyle: "normal",
          color: BLUE_ACCENT,
          bgcolor: `${INFO_TINT}, 0.12)`,
          borderRadius: "50%",
          cursor: "pointer",
          verticalAlign: "super",
          position: "relative",
          top: "-2px",
          lineHeight: 1,
          transition: "all 0.2s ease",
          border: `1px solid ${INFO_TINT}, 0.25)`,
          "&:hover": {
            bgcolor: `${INFO_TINT}, 0.25)`,
            transform: "scale(1.15)",
          },
          "&:focus-visible": {
            outline: `2px solid ${BLUE_ACCENT}`,
            outlineOffset: 2,
          },
        }}
      >
        i
      </SoftBox>
      {open && (
        <TooltipOverlay
          tooltip={tooltip}
          position={position}
          onClose={handleClose}
          isMobile={isMobile}
        />
      )}
    </>
  );
}

InfoTooltip.propTypes = {
  tooltipId: PropTypes.string.isRequired,
  inline: PropTypes.bool,
};

// Renders an array of inline rich-text segments
function renderRichText(segments) {
  return segments.map((seg, i) => {
    const style = {
      fontWeight: seg.bold ? 700 : undefined,
      textDecoration: seg.underline ? "underline" : undefined,
      fontStyle: seg.italic ? "italic" : undefined,
      color: seg.color || undefined,
      backgroundColor: seg.highlight || undefined,
    };
    if (seg.href) {
      return (
        <a
          key={i}
          href={seg.href}
          target={seg.href.startsWith("tel:") ? "_self" : "_blank"}
          rel="noopener noreferrer"
          style={{ color: seg.color || BLUE_ACCENT, textDecoration: seg.underline ? "underline" : "underline", fontWeight: seg.bold ? 700 : undefined, fontStyle: seg.italic ? "italic" : undefined }}
        >
          {seg.text}
        </a>
      );
    }
    if (seg.tooltipId) {
      return (
        <span key={i} style={style}>
          {seg.text}
          <InfoTooltip tooltipId={seg.tooltipId} />
        </span>
      );
    }
    if (seg.bold || seg.underline || seg.italic || seg.color || seg.highlight) {
      return <span key={i} style={style}>{seg.text}</span>;
    }
    return seg.text;
  });
}

// Renders tooltip content (text with embedded tooltips)
function renderTooltipContent(segments) {
  return segments.map((seg, i) => {
    if (seg.tooltipId) {
      return (
        <span key={i} style={{ display: "inline" }}>
          {seg.text}
          <InfoTooltip tooltipId={seg.tooltipId} inline />
        </span>
      );
    }
    return <span key={i}>{seg.text}</span>;
  });
}

function ContentDisplay({ content, subsectionContent, subsubsectionContent, onSectionChange }) {
  const displayContent = subsubsectionContent || subsectionContent || content;

  if (!displayContent) {
    return (
      <SoftBox textAlign="center" py={10}>
        <SoftTypography variant="h5" color="text">
          Selectează o secțiune din meniu
        </SoftTypography>
      </SoftBox>
    );
  }

  return (
    <SoftBox>
      <SoftTypography variant="h4" fontWeight="bold" sx={{ color: TITLE_COLOR }} mb={2}>
        {displayContent.title}
      </SoftTypography>

      <Divider sx={{ my: 3 }} />

      {displayContent.sections && displayContent.sections.map((section, index) => (
        <SoftBox key={index} mb={4}>
          {section.heading && (
            <SoftBox display="flex" alignItems="center" gap={1} mb={2}>
              <SoftTypography variant="h5" fontWeight="bold" sx={{ color: TITLE_COLOR }}>
                {section.heading}
              </SoftTypography>
              {section.tooltipId && <InfoTooltip tooltipId={section.tooltipId} />}
            </SoftBox>
          )}

          {section.content && (
            <SoftTypography variant="body1" color="text" lineHeight={1.8} mb={2}>
              {section.content}
            </SoftTypography>
          )}

          {section.richContent && (
            <SoftTypography variant="body1" color="text" lineHeight={1.8} mb={2} component="p">
              {renderRichText(section.richContent)}
            </SoftTypography>
          )}

          {section.warning && (
            <SoftBox
              mb={2}
              p={2}
              borderRadius="lg"
              sx={{
                bgcolor: "rgba(255, 152, 0, 0.08)",
                border: "1px solid rgba(255, 152, 0, 0.3)",
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
              }}
            >
              <Icon sx={{ color: "#f57c00", fontSize: "1.25rem !important", mt: "2px", flexShrink: 0 }}>warning</Icon>
              <SoftTypography variant="body2" color="text" lineHeight={1.8}>
                {section.warning}
              </SoftTypography>
            </SoftBox>
          )}

          {section.tooltipContent && (
            <SoftTypography variant="body1" color="text" lineHeight={1.8} mb={2} component="p">
              {renderTooltipContent(section.tooltipContent)}
            </SoftTypography>
          )}

          {section.richTooltipList && (
            <SoftBox component="ul" pl={3} mb={2}>
              {section.richTooltipList.map((item, idx) => (
                <SoftBox component="li" key={idx} mb={1}>
                  <SoftTypography variant="body2" color="text" lineHeight={1.8} component="span">
                    {Array.isArray(item) ? renderTooltipContent(item) : item}
                  </SoftTypography>
                </SoftBox>
              ))}
            </SoftBox>
          )}

          {section.tooltipList && (
            <SoftBox component="ul" pl={3} mb={2}>
              {section.tooltipList.map((item, idx) => (
                <SoftBox component="li" key={idx} mb={1}>
                  <SoftTypography variant="body2" color="text" lineHeight={1.8} component="span">
                    {item.text}
                    {item.tooltipId && <InfoTooltip tooltipId={item.tooltipId} inline />}
                  </SoftTypography>
                </SoftBox>
              ))}
            </SoftBox>
          )}

          {section.list && (
            <SoftBox component="ul" pl={3} mb={2}>
              {section.list.map((item, idx) => (
                <SoftBox component="li" key={idx} mb={1}>
                  <SoftTypography variant="body2" color="text" lineHeight={1.8}>
                    {item}
                  </SoftTypography>
                </SoftBox>
              ))}
            </SoftBox>
          )}

          {section.subsectionLinks && (
            <SoftBox component="ul" pl={3} mb={2}>
              {section.subsectionLinks.map((link, idx) => (
                <SoftBox component="li" key={idx} mb={1}>
                  <SoftTypography
                    variant="body2"
                    component="a"
                    onClick={() => onSectionChange(link.id)}
                    sx={{
                      color: BLUE_ACCENT,
                      cursor: "pointer",
                      textDecoration: "none",
                      lineHeight: 1.8,
                      "&:hover": {
                        textDecoration: "underline",
                        color: BLUE_DARK,
                      },
                    }}
                  >
                    {link.title}
                  </SoftTypography>
                </SoftBox>
              ))}
            </SoftBox>
          )}

          {section.richList && (
            <SoftBox component="ul" pl={3} mb={2}>
              {section.richList.map((item, idx) => {
                if (Array.isArray(item)) {
                  return (
                    <SoftBox component="li" key={idx} mb={1}>
                      <SoftTypography variant="body2" color="text" lineHeight={1.8} component="span">
                        {renderRichText(item)}
                      </SoftTypography>
                    </SoftBox>
                  );
                }
                if (typeof item === "object" && item !== null && !Array.isArray(item)) {
                  return (
                    <SoftBox component="li" key={idx} mb={1}>
                      <SoftTypography variant="body2" color="text" lineHeight={1.8} component="span">
                        {Array.isArray(item.content) ? renderRichText(item.content) : item.content}
                      </SoftTypography>
                      {item.subItems && (
                        <SoftBox component="ul" pl={3} mt={0.5}>
                          {item.subItems.map((sub, subIdx) => (
                            <SoftBox component="li" key={subIdx} mb={0.5}>
                              <SoftTypography variant="body2" color="text" lineHeight={1.8} component="span">
                                {Array.isArray(sub) ? renderRichText(sub) : sub}
                              </SoftTypography>
                            </SoftBox>
                          ))}
                        </SoftBox>
                      )}
                    </SoftBox>
                  );
                }
                return (
                  <SoftBox component="li" key={idx} mb={1}>
                    <SoftTypography variant="body2" color="text" lineHeight={1.8}>
                      {item}
                    </SoftTypography>
                  </SoftBox>
                );
              })}
            </SoftBox>
          )}

          {section.arrowList && (
            <SoftBox component="ul" sx={{ listStyle: "none", pl: 0, mb: 2 }}>
              {section.arrowList.map((item, idx) => (
                <SoftBox component="li" key={idx} mb={1.5} display="flex" alignItems="flex-start" gap={1.5}>
                  <SoftTypography
                    variant="body2"
                    sx={{ color: BLUE_ACCENT, fontWeight: 700, fontSize: "1rem", lineHeight: 1.8, flexShrink: 0 }}
                  >
                    ▶
                  </SoftTypography>
                  <SoftTypography variant="body2" color="text" lineHeight={1.8}>
                    {item}
                  </SoftTypography>
                </SoftBox>
              ))}
            </SoftBox>
          )}

          {section.quote && (
            <SoftBox
              sx={{
                borderLeft: `4px solid ${NAVY_MID}`,
                bgcolor: `${NAVY_TINT}, 0.05)`,
                p: 2,
                my: 2,
                borderRadius: "4px",
              }}
            >
              <SoftTypography variant="body2" color="text" fontStyle="italic" lineHeight={1.8}>
                {section.quote}
              </SoftTypography>
            </SoftBox>
          )}

          {section.links && section.links.length > 0 && (
            <SoftBox mt={1} mb={1}>
              {section.links.map((link, linkIdx) => (
                <SoftBox key={linkIdx} display="inline-flex" alignItems="center" mr={2}>
                  <Icon sx={{ fontSize: "0.9rem !important", color: BLUE_ACCENT, mr: 0.5 }}>open_in_new</Icon>
                  <SoftTypography
                    component="a"
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                    sx={{
                      color: BLUE_ACCENT,
                      textDecoration: "underline",
                      cursor: "pointer",
                      "&:hover": { color: BLUE_DARK },
                    }}
                  >
                    {link.text}
                  </SoftTypography>
                </SoftBox>
              ))}
            </SoftBox>
          )}

          {/* Resource Links for Resurse section */}
          {section.resourceLinks && section.resourceLinks.length > 0 && (
            <SoftBox mb={2}>
              {section.resourceLinks.map((resource, idx) => (
                <SoftBox
                  key={idx}
                  component="a"
                  href={resource.href}
                  target={resource.type === "external" ? "_blank" : "_self"}
                  rel={resource.type === "external" ? "noopener noreferrer" : undefined}
                  download={resource.type === "pdf" || resource.type === "docx" ? true : undefined}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.5,
                    mb: 1,
                    borderRadius: "8px",
                    border: "1px solid",
                    borderColor: `${INFO_TINT}, 0.2)`,
                    bgcolor: `${INFO_TINT}, 0.03)`,
                    textDecoration: "none",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: `${INFO_TINT}, 0.08)`,
                      borderColor: `${INFO_TINT}, 0.4)`,
                      transform: "translateX(4px)",
                    },
                  }}
                >
                  <Icon
                    sx={{
                      fontSize: "1.3rem !important",
                      color: resource.type === "pdf" ? "#E53935" : resource.type === "docx" ? BLUE_ACCENT : BLUE_ACCENT,
                      flexShrink: 0,
                    }}
                  >
                    {resource.type === "pdf" ? "picture_as_pdf" : resource.type === "docx" ? "description" : "open_in_new"}
                  </Icon>
                  <SoftBox flex={1}>
                    <SoftTypography
                      variant="body2"
                      sx={{
                        color: TITLE_COLOR,
                        fontWeight: 500,
                        lineHeight: 1.5,
                      }}
                    >
                      {resource.title}
                    </SoftTypography>
                    <SoftTypography
                      variant="caption"
                      sx={{
                        color: resource.type === "pdf" ? "#E53935" : resource.type === "docx" ? BLUE_ACCENT : BLUE_ACCENT,
                        fontWeight: 500,
                        textTransform: "uppercase",
                        fontSize: "0.65rem",
                      }}
                    >
                      {resource.type === "pdf" ? "PDF" : resource.type === "docx" ? "DOCX" : "Link extern"}
                    </SoftTypography>
                  </SoftBox>
                  <Icon
                    sx={{
                      fontSize: "1rem !important",
                      color: "#9e9e9e",
                      flexShrink: 0,
                    }}
                  >
                    {resource.type === "external" ? "open_in_new" : "download"}
                  </Icon>
                </SoftBox>
              ))}
            </SoftBox>
          )}
        </SoftBox>
      ))}
    </SoftBox>
  );
}

const richSegmentShape = PropTypes.shape({
  text: PropTypes.string,
  bold: PropTypes.bool,
  underline: PropTypes.bool,
  italic: PropTypes.bool,
  color: PropTypes.string,
  highlight: PropTypes.string,
  href: PropTypes.string,
});

const sectionShape = PropTypes.shape({
  heading: PropTypes.string,
  content: PropTypes.string,
  warning: PropTypes.string,
  tooltipId: PropTypes.string,
  tooltipContent: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string,
    tooltipId: PropTypes.string,
  })),
  tooltipList: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string,
    tooltipId: PropTypes.string,
  })),
  richContent: PropTypes.arrayOf(richSegmentShape),
  list: PropTypes.arrayOf(PropTypes.string),
  richList: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(richSegmentShape)])),
  arrowList: PropTypes.arrayOf(PropTypes.string),
  quote: PropTypes.string,
  links: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string,
    href: PropTypes.string,
  })),
});

ContentDisplay.propTypes = {
  content: PropTypes.shape({ title: PropTypes.string, sections: PropTypes.arrayOf(sectionShape) }),
  subsectionContent: PropTypes.shape({ title: PropTypes.string, sections: PropTypes.arrayOf(sectionShape) }),
  subsubsectionContent: PropTypes.shape({ title: PropTypes.string, sections: PropTypes.arrayOf(sectionShape) }),
  onSectionChange: PropTypes.func,
};

export default ContentDisplay;
